import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Loader2, Printer, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicReceipt() {
  const { invoiceId } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchReceipt();
  }, [invoiceId]);

  const fetchReceipt = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/receipt/${invoiceId}`);
      if (response.ok) {
        const data = await response.json();
        setReceipt(data);
      } else {
        const err = await response.json();
        setError(err.detail || 'Receipt not found');
      }
    } catch (err) {
      setError('Failed to load receipt');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Receipt Not Found</h1>
          <p className="text-gray-600">{error}</p>
        </Card>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      {/* Action Bar - Hidden in print */}
      <div className="max-w-4xl mx-auto mb-6 flex justify-between items-center print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payment Receipt</h1>
          <p className="text-gray-600">#{receipt.receipt_number}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* Receipt Document */}
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden print:shadow-none">
        {/* Header */}
        <div className="px-8 py-6 bg-green-600 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold">{receipt.business.name}</h2>
              {receipt.business.email && (
                <p className="text-green-100 mt-1">{receipt.business.email}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-green-100 text-sm uppercase tracking-wider">Receipt</p>
              <p className="text-2xl font-bold">#{receipt.receipt_number}</p>
              <span className="inline-flex items-center gap-1 mt-2 bg-white/20 px-3 py-1 rounded-full text-sm">
                <CheckCircle className="h-4 w-4" />
                PAID
              </span>
            </div>
          </div>
        </div>

        {/* Thank You Message */}
        <div className="px-8 py-8 bg-green-50 text-center border-b">
          <div className="text-4xl mb-3">🎉</div>
          <h3 className="text-xl font-bold text-green-800">Thank you for your payment!</h3>
          <p className="text-green-600 mt-1">Your payment has been received and processed.</p>
        </div>

        {/* Details */}
        <div className="px-8 py-6 border-b">
          <div className="grid grid-cols-2 gap-8">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Paid By</p>
              <p className="font-semibold text-gray-900">{receipt.client.name}</p>
              {receipt.client.email && <p className="text-gray-600">{receipt.client.email}</p>}
            </div>
            <div className="text-right">
              <div className="mb-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Payment Date</p>
                <p className="text-gray-900">{formatDate(receipt.payment_date)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Payment Method</p>
                <p className="text-gray-900">{receipt.payment_method}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Reference */}
        <div className="px-8 py-4 bg-gray-50 border-b">
          <p className="text-sm text-gray-500">
            Reference: Invoice #{receipt.invoice_number}
          </p>
        </div>

        {/* Items Table */}
        <div className="px-8 py-6">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-3 text-xs text-gray-500 uppercase tracking-wider">Description</th>
                <th className="text-center py-3 text-xs text-gray-500 uppercase tracking-wider">Qty</th>
                <th className="text-right py-3 text-xs text-gray-500 uppercase tracking-wider">Amount</th>
              </tr>
            </thead>
            <tbody>
              {receipt.items.map((item, index) => (
                <tr key={index} className="border-b border-gray-100">
                  <td className="py-4 text-gray-900">{item.description}</td>
                  <td className="py-4 text-center text-gray-600">{item.quantity}</td>
                  <td className="py-4 text-right text-gray-900 font-medium">{formatCurrency(item.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="px-8 py-6 bg-gray-50">
          <div className="flex justify-end">
            <div className="w-64">
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Subtotal</span>
                <span className="text-gray-900">{formatCurrency(receipt.subtotal)}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Tax</span>
                <span className="text-gray-900">{formatCurrency(receipt.tax)}</span>
              </div>
              <div className="flex justify-between py-3 border-t-2 border-green-500">
                <span className="text-lg font-bold text-gray-900">Amount Paid</span>
                <span className="text-lg font-bold text-green-600">
                  {formatCurrency(receipt.amount_paid)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Message */}
        <div className="px-8 py-6 border-t text-center">
          <p className="text-gray-500 text-sm">
            This receipt confirms your payment to {receipt.business.name}.
          </p>
          <p className="text-gray-400 text-xs mt-2">
            Please keep this receipt for your records.
          </p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .print\\:shadow-none { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}
